import { useMemo } from "react";

import { BTButton } from "@btnet/core-components/BTButton";
import { BTText } from "@btnet/core-components/BTText";

import { type BuilderInfo } from "helpers/AppProvider.types";

import {
    getEntityMeta,
    getHitKey,
} from "commonComponents/utilities/MainNavigation/SearchDialog/entity/entityMeta";
import {
    extractSubtitle,
    extractTitle,
} from "commonComponents/utilities/MainNavigation/SearchDialog/entity/hitText";
import { useEntityNavigation } from "commonComponents/utilities/MainNavigation/SearchDialog/entity/useEntityNavigation";
import type { ISearchHit } from "commonComponents/utilities/MainNavigation/SearchDialog/search/search.types";

import { readAmount, readStatus } from "../rows/rowFields";
import { type ISearchDialogSection, SearchDialogList } from "../rows/SearchDialogList";
import type { ISearchDialogRow } from "../rows/searchDialogRow.types";
import type { NavMethod } from "../searchDialog.learn";
import { highlightMatches } from "./highlight";
import { isRetryableSearchError, type ResultsDisplay, SEARCH_ERROR_COPY } from "./resultsDisplay";

export interface IUseResultSectionsArgs {
    display: ResultsDisplay;
    query: string;
    builderInfo: BuilderInfo | null | undefined;
    /** Called after navigating, so the dialog can close and record the visit. */
    onResultOpened: (
        hit: ISearchHit,
        title: string,
        subtitle: string | null,
        rank: number,
        navMethod: NavMethod,
    ) => void;
}

/**
 * Rows for the service's committed ranking; previews never render. A pending
 * query keeps the last ranking, inert (see `resolveResultsDisplay`).
 */
export function useResultSections(args: IUseResultSectionsArgs): ISearchDialogSection[] {
    const { display, query, builderInfo, onResultOpened } = args;
    const { isRoutable, open } = useEntityNavigation(builderInfo);

    return useMemo<ISearchDialogSection[]>(() => {
        const isHeld = display.kind === "held";
        const ranked = display.kind === "rows" || isHeld ? display.ranked : [];
        const rows: ISearchDialogRow[] = ranked
            // The service indexes records the SPA has no page for, and a row
            // that does nothing when clicked is worse than one row fewer.
            .filter((hit) => isRoutable(hit))
            .map((hit, index) => {
                const title = extractTitle(hit);
                const subtitle = extractSubtitle(hit);
                return {
                    key: getHitKey(hit),
                    kind: "record" as const,
                    icon: getEntityMeta(hit.entity).icon,
                    title: query ? highlightMatches(title, query) : title,
                    subtitle,
                    amount: readAmount(hit),
                    status: readStatus(hit),
                    trailingLabel: getEntityMeta(hit.entity).label,
                    // Held rows belong to the query before this one.
                    onSelect: isHeld
                        ? null
                        : (navMethod = "click") => {
                              open(hit);
                              // 1-based rank in the rendered (routable) list.
                              onResultOpened(hit, title, subtitle, index + 1, navMethod);
                          },
                };
            });

        // Unlabelled: each row names its own kind, and a lone header adds nothing.
        return [{ label: null, rows }];
    }, [display, query, isRoutable, open, onResultOpened]);
}

export interface IResultsViewProps {
    /** What to show, resolved by the dialog from the search state and the live query. */
    display: ResultsDisplay;
    sections: ISearchDialogSection[];
    query: string;
    highlightedIndex: number;
    onHoverRow: (index: number) => void;
    /** The service's suggested spelling, or null. */
    corrected: string | null;
    /** Re-runs the search with the correction. */
    onApplyCorrection: (corrected: string) => void;
    /** Runs the failed search again. */
    onRetry: () => void;
    /**
     * While searching, how many records have come back so far: the ranking is
     * held until it lands, but a slow one should still show progress.
     */
    foundSoFar?: number;
}

/**
 * "Did you mean X?" — above the results and above the empty state, since a typo
 * is most worth correcting when nothing matched.
 */
function DidYouMean(props: { corrected: string | null; onApply: (value: string) => void }) {
    if (props.corrected === null) {
        return null;
    }
    return (
        <div
            className="flex items-center gap-1 border-b border-default px-4 py-2"
            data-testid="search-dialog-did-you-mean"
        >
            <BTText size="normal-sm" tag="span" className="text-fg-tertiary">
                Did you mean
            </BTText>
            <BTButton
                type="tertiary"
                onClick={() => props.onApply(props.corrected as string)}
                data-testid="search-dialog-did-you-mean-apply"
            >
                {props.corrected}
            </BTButton>
        </div>
    );
}

export function ResultsView(props: IResultsViewProps) {
    const {
        display,
        sections,
        query,
        highlightedIndex,
        onHoverRow,
        corrected,
        onApplyCorrection,
        onRetry,
        foundSoFar = 0,
    } = props;
    const isHeld = display.kind === "held";

    const didYouMean = <DidYouMean corrected={corrected} onApply={onApplyCorrection} />;

    if (display.kind === "searching") {
        // No ranking for this query yet; previews are never shown.
        return (
            <>
                {didYouMean}
                <BTText
                    size="normal-sm"
                    tag="p"
                    className="m-0 px-4 py-6 text-fg-tertiary"
                    testid="search-dialog-searching"
                >
                    {foundSoFar > 0
                        ? `Searching… ${foundSoFar.toLocaleString()} found so far`
                        : "Searching…"}
                </BTText>
            </>
        );
    }
    if (display.kind === "error") {
        return (
            <div
                className="flex items-center justify-between gap-3 px-4 py-6"
                data-testid="search-dialog-error"
            >
                <BTText size="normal-sm" tag="p" className="m-0 text-fg-error">
                    {SEARCH_ERROR_COPY[display.errorKind]}
                </BTText>
                {isRetryableSearchError(display.errorKind) && (
                    <BTButton type="secondary" onClick={onRetry} data-testid="search-dialog-retry">
                        Retry
                    </BTButton>
                )}
            </div>
        );
    }
    if (display.kind === "empty") {
        // Names what was looked through and points at the alternative, rather
        // than leaving the user to guess why nothing came back. Ask is armed by
        // now (see resolveAskMode), so Enter asks the question.
        return (
            <>
                {didYouMean}
                <div className="flex flex-col gap-1 px-4 py-6" data-testid="search-dialog-empty">
                    <BTText size="distinct-md" tag="p" className="m-0 text-fg-default">
                        Nothing named &ldquo;{query}&rdquo;
                    </BTText>
                    <BTText size="normal-sm" tag="p" className="m-0 text-fg-tertiary">
                        No job, bill, task or document in this workspace goes by that name. To
                        describe what you want instead of naming it, select Ask.
                    </BTText>
                </div>
            </>
        );
    }

    if (isHeld) {
        return (
            <>
                {didYouMean}
                {/* Hidden from assistive tech, which hears "Searching" from
                    the dialog's status line instead of a list that is about
                    to change. The rows are inert; swallowing mousedown keeps
                    a click on one from taking focus out of the input. */}
                <div
                    className="opacity-50"
                    aria-hidden="true"
                    onMouseDown={(event) => event.preventDefault()}
                    data-results-held="true"
                    data-testid="search-dialog-results-held"
                >
                    <SearchDialogList sections={sections} highlightedIndex={-1} />
                </div>
            </>
        );
    }

    return (
        <>
            {didYouMean}
            <SearchDialogList
                sections={sections}
                highlightedIndex={highlightedIndex}
                onHoverRow={onHoverRow}
            />
        </>
    );
}
